import { __awaiter, __decorate, __param } from "tslib";
import { DI, optional } from '@microsoft/fast-foundation';
import { Connect } from '../../connect';
import { logger } from '../../utils';
import { defaultKVStorageConfig, KVStorageConfig } from './kv-config.service';
import { KVEventMessageType, KVMessageType, } from './kv.types';
/**
 * The default implementation for the KVStorage interface.
 * @alpha
 */
let DefaultKVStorage = class DefaultKVStorage {
    constructor(connect, kvStorageConfig = defaultKVStorageConfig) {
        this.connect = connect;
        this.kvStorageConfig = kvStorageConfig;
    }
    /**
     * Deletes the specified key-value pairs from the KV storage.
     * @alpha
     * @param kvs - The key names to delete.
     * @returns A promise that resolves to true if the operation was successful, or an object containing error and warning information if the operation failed.
     * @example
     * ```ts
     *  ...
     *  const result = await this.kvStorage.delete(['myKey']);
     *  logger.debug(result); // true or { ERROR: ..., WARNING: ... }
     *  ...
     * ```
     */
    delete(keys) {
        return __awaiter(this, void 0, void 0, function* () {
            const params = {
                DETAILS: {
                    KEYS: keys,
                },
            };
            const result = yield this.connect.commitEvent(KVEventMessageType.EVENT_KEY_VALUE_DELETE, params);
            logger.debug('KV Delete result: ', result);
            return new Promise((resolve) => {
                if (result && result.MESSAGE_TYPE === 'EVENT_ACK') {
                    resolve(true);
                }
                else {
                    resolve({ ERROR: result.ERROR, WARNING: result.WARNING });
                }
            });
        });
    }
    /**
     * Gets the specified key-value pairs from the KV storage.
     * @remarks If no key is specified, all key-value pairs for the current user are returned.
     * @alpha
     * @param kv - The single key-value pair to get.
     * @returns A promise that resolves to an array of key-value pairs.
     *
     * @example
     * ```ts
     * ...
     *  const result = await this.kvStorage.get({ key: 'myKey' });
     *  logger.debug(result); // [{ key: 'myKey', value: 'myValue' }]
     *
     *  const result2 = await this.kvStorage.get();
     *  logger.debug(result); // [{ key: 'myKey', value: 'myValue' }, { key: 'myOtherKey', value: 'myOtherValue' }]
     *  ...
     * ```
     */
    get(key) {
        return __awaiter(this, void 0, void 0, function* () {
            const params = {
                REQUEST: {
                    USER_NAME: this.kvStorageConfig.authEmail,
                    KEY: key,
                },
            };
            const result = yield this.connect.request(KVMessageType.REQ_KEY_VALUE_READ, params);
            logger.debug('KV Get result: ', result);
            return new Promise((resolve) => {
                if (result && result.REPLY && result.REPLY.length > 0) {
                    const kvs = result.REPLY.map((kv) => ({ kv: { key: kv.KEY, value: kv.VALUE } }));
                    resolve(kvs);
                }
                else {
                    resolve([]);
                }
            });
        });
    }
    /**
     * Lists all keys for the user.
     * @alpha
     * @returns A promise that resolves to all keys for the user.
     * @example
     * ```ts
     *  ...
     *  const result = await this.kvStorage.list();
     *  logger.debug(result); // ['myKey', 'myOtherKey']
     *  ...
     * ```
     */
    list() {
        return __awaiter(this, void 0, void 0, function* () {
            const params = {
                REQUEST: {
                    USER_NAME: this.kvStorageConfig.authEmail,
                },
            };
            const result = yield this.connect.request(KVMessageType.REQ_KEY_VALUE_LIST, params);
            logger.debug('KV List result: ', result);
            return new Promise((resolve) => {
                if (result && result.REPLY && result.REPLY.length > 0) {
                    const keys = result.REPLY.map((kv) => kv.KEY);
                    resolve(keys);
                }
                else {
                    resolve([]);
                }
            });
        });
    }
    /**
     * Inserts or updates the specified key-value pairs in the KV storage.
     * @alpha
     * @param kvs - The key-value pairs to insert or update.
     * @returns A promise that resolves to true if the operation was successful, or an object containing error and warning information if the operation failed.
     * @example
     * ```ts
     *  ...
     *  const result = await this.kvStorage.put([{ key: 'myKey', value: 'myValue' }]);
     *  logger.debug(result); // true or { ERROR: ..., WARNING: ... }
     *  ...
     * ```
     */
    put(kvs) {
        return __awaiter(this, void 0, void 0, function* () {
            const params = {
                DETAILS: {
                    RECORDS: kvs.map((kv) => ({
                        KEY: kv.key,
                        VALUE: kv.value,
                    })),
                },
            };
            const result = yield this.connect.commitEvent(KVEventMessageType.EVENT_KEY_VALUE_PUT, params);
            logger.debug('KV Put result: ', result);
            return new Promise((resolve) => {
                if (result && result.MESSAGE_TYPE === 'EVENT_ACK') {
                    resolve(true);
                }
                else {
                    resolve({ ERROR: result.ERROR, WARNING: result.WARNING });
                }
            });
        });
    }
};
DefaultKVStorage = __decorate([
    __param(0, Connect),
    __param(1, optional(KVStorageConfig))
], DefaultKVStorage);
export { DefaultKVStorage };
/**
 * The DI token for the KVStorage interface.
 * @internal
 */
export const KVStorage = DI.createInterface((x) => x.singleton(DefaultKVStorage));
